import React, { useEffect, useState, useContext } from 'react';
import WithStandardLayout from '@/layouts/withStandard.layout';
import Header from '@/components/header/Header';
import Footer from '@/components/footer/Footer';
import Assessments from '@/components/Assessments/Assessments';
import Documents from '@/components/Documents/Documents';
import Collections from '@/components/Collections/Collections';
import TabNav from '@/components/TabNav/TabNav';
import PlanList from '@/components/PlanList/PlanList';
import initializeIconLibrary from '@/utils/fontAwesomeLibrary';
import { faHeart } from '@fortawesome/free-solid-svg-icons';
import 'bootstrap/dist/css/bootstrap.min.css';
import t from '@/utils/Language/UI-translations';
import find from 'lodash/find';
import useAccount from '@/hooks/useAccount';
import useAssessments from '@/hooks/useAssessments';
import { useRouter } from 'next/router';
import { modalContext } from '@/context/modal.context';
import Auth from '@/utils/api/odas/auth';
import useReAuth from '@/hooks/useReAuth';
import { buildServerSidePropsWithReqParams } from '@/utils/server';

initializeIconLibrary();

/**
 * Sample page for now to see the header, needs to be refactored when ready to a layout, or something more reusable
 *
 * @returns {Component}
 */
const PlanPage = ({ query }) => {
  const { account } = useAccount();
  const [tabs, setTabs] = useState([]);
  const [currentTab, setCurrentTab] = useState();
  const router = useRouter();
  const { assessments } = useAssessments();
  const { login } = router.query;
  const { enqueueModal } = useContext(modalContext);
  useReAuth();

  useEffect(() => {
    if (login === '1' && !Auth.isLoggedIn()) {
      enqueueModal({ name: 'LoginModal', reason: 'login', backdrop: 'static', openModal: true }, { forceOpen: true });
    }
    if (Auth.isLoggedIn()) {
      account.getVaultId();
    }
  }, [account.state?.isLoggedIn]);

  useEffect(() => {
    if (assessments.length) {
      setTabs(pageTabs());
    }
  }, [assessments]);

  const pageTabs = (tabKey) => {
    const tabs = [
      {
        title: 'Resources',
        key: 'resources',
        Component: <PlanList showTitle={false} showFullPlan={true} />
      },
      {
        title: 'Collections',
        key: 'collections',
        Component: <Collections urlQueryParameters={Object.assign({}, query, router.query)} />
      },
      {
        title: 'Assessments',
        key: 'assessments',
        hidden: assessments.length == 0,
        Component: <Assessments />
      },
      {
        title: 'Documents',
        key: 'documents',
        Component: <Documents vaultId={account.vaultId} />
      }
    ];
    if (tabKey) {
      const found_tab = find(tabs, (tab) => {
        return tab.key == tabKey;
      });
      return found_tab || tabs[0];
    } else {
      return tabs;
    }
  };

  const getTabFromHash = (hash) => {
    return hash.slice(1, hash.length);
  };

  // On this page, the hash will be changed in one of two ways from the site menu (Header.js)
  // 1. From the href of a Bootstrap Dropdown Menu elements or
  // 2. From the href of a Next Link component
  // In the case of the Next Link updating the hash, the event must be detected from Next router
  // but in the case of the Boostrap Dropdown Menu element, the event must be detected from the window
  // So the following methods handle both cases separately to ensure the tab will always get updated from a hash change.
  const hashChangeHandler = React.useCallback(() => {
    setCurrentTab(getTabFromHash(window.location.hash));
  }, []);

  useEffect(() => {
    router.events.on('hashChangeComplete', hashChangeHandler);
    return () => {
      router.events.off('hashChangeComplete', hashChangeHandler);
    };
  }, [router.events]);

  useEffect(() => {
    setTabs(pageTabs());
    window.addEventListener('hashchange', hashChangeHandler);
    return () => {
      window.removeEventListener('hashchange', hashChangeHandler);
    };
  }, []);

  useEffect(() => {
    if (currentTab) {
      window.location.hash = '#' + currentTab;
    }
  }, [currentTab]);

  useEffect(() => {
    // set the tab from the URL #anchor on page load
    if (window.location.hash) {
      setCurrentTab(getTabFromHash(window.location.hash));
    }
  }, [account.state.isLoggedIn]);

  return (
    <>
      <Header />
      <TabNav
        tabs={tabs}
        t={t} // translations
        currentTab={currentTab ? pageTabs(currentTab) : pageTabs()[0]}
        setCurrentTab={setCurrentTab}
        title={'My Plan'}
        icon={faHeart}
      />
      <Footer />
    </>
  );
};

export const getServerSideProps = buildServerSidePropsWithReqParams({ query: {} });

// https://nextjs.org/docs/basic-features/layouts
// Layouts can be nested, we could use that functionality to make sure we include the Providers
// and data that every page requires
PlanPage.getLayout = function getLayout(page) {
  return <WithStandardLayout host={page.props.host}>{page}</WithStandardLayout>;
};

export default PlanPage;
